'use client';

import Image from 'next/image';
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import StepSelector from '../shared/StepSelector';
import AnimatedGradientButton from '../shared/AnimatedGradientButton';

// Import images directly for optimization
import step1Image from './images/1.png';
import step2Image from './images/2.png';
import step3Image from './images/3.png';
import { useIsMobile } from '@/hooks/useIsMobile';

interface Step {
  label: string;
  description: string | React.ReactElement;
}

interface TestInstructionsClientProps {
  steps: Step[];
}

// Slide content component with optimized images
interface SlideContentProps {
  stepIndex: number;
}

function SlideContent({ stepIndex }: SlideContentProps) {
  const [isVideoLoading, setIsVideoLoading] = React.useState(true);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  React.useEffect(() => {
    if (stepIndex >= 3) {
      setIsVideoLoading(true);
    }
  }, [stepIndex]);

  // Close fullscreen on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when fullscreen is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  if (stepIndex < 3) {
    // Get the appropriate image and create blur data URL
    const images = [step1Image, step2Image, step3Image];
    const currentImage = images[stepIndex];

    return (
      <>
        <Image
          className="h-auto w-full max-w-full cursor-pointer rounded-[10px] object-contain sm:max-h-[300px] sm:cursor-default md:max-h-[400px] lg:max-h-[500px]"
          src={currentImage.src}
          height={417}
          width={741}
          alt={`Test instruction step ${stepIndex + 1}`}
          placeholder="blur"
          blurDataURL={currentImage.blurDataURL}
          priority={true} // Prioritize loading the first image
          onClick={() => {
            // Only enable fullscreen on mobile (below sm breakpoint)
            if (window.innerWidth < 640) {
              setIsFullscreen(true);
            }
          }}
        />

        {/* Fullscreen Modal - Mobile Only */}
        {isFullscreen && (
          <div
            className="bg-opacity-90 fixed inset-0 z-50 flex items-center justify-center bg-black sm:hidden"
            onClick={() => setIsFullscreen(false)}
          >
            <div className="relative h-full w-full p-4">
              <button
                onClick={e => {
                  e.stopPropagation();
                  setIsFullscreen(false);
                }}
                className="bg-opacity-20 hover:bg-opacity-30 absolute top-4 right-4 z-10 rounded-full bg-white p-2 text-white"
                aria-label="Close fullscreen"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <div
                className="flex h-full items-center justify-center"
                onClick={e => e.stopPropagation()}
              >
                <Image
                  className="max-h-full max-w-full object-contain"
                  src={currentImage.src}
                  height={417}
                  width={741}
                  alt={`Test instruction step ${stepIndex + 1} - Fullscreen`}
                  placeholder="blur"
                  blurDataURL={currentImage.blurDataURL}
                />
              </div>
              <div className="absolute right-4 bottom-4 left-4 text-center">
                <p className="text-sm text-white opacity-80">
                  Tap anywhere or press ESC to close
                </p>
              </div>
            </div>
          </div>
        )}
      </>
    );
  } else {
    return (
      <div className="w-full">
        <div
          className="relative w-full overflow-hidden rounded-[10px]"
          style={{ paddingBottom: '56.25%' }}
        >
          {/* Background placeholder that matches iframe styling */}
          <div className="absolute inset-0 rounded-[10px] border-2 border-gray-200 bg-linear-to-br from-gray-50 to-gray-100 shadow-sm">
            {/* Loading overlay */}
            {isVideoLoading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-[10px] bg-black/5 backdrop-blur-[1px] transition-opacity duration-300">
                <div className="flex flex-col items-center space-y-4">
                  <div className="h-12 w-12 animate-spin rounded-full border-4 border-orange-200 border-t-orange-500"></div>
                  <p className="text-sm font-medium text-gray-600">
                    Loading video...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Video iframe */}
          <iframe
            src="https://player.vimeo.com/video/1075144739?h=457b68d49f&badge=0&autopause=0&player_id=0&app_id=58479"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            style={{
              height: '100%',
              left: 0,
              position: 'absolute',
              top: 0,
              width: '100%',
            }}
            title="trusti Test Instructions"
            onLoad={() => setIsVideoLoading(false)}
            className="rounded-[10px]"
          />
        </div>
      </div>
    );
  }
}

function SliderContent(props: { step: number; direction: number }) {
  const slideVariants = {
    center: {
      opacity: 1,
      scale: 1,
      x: '0%',
    },
    enter: (direction: number) => ({
      opacity: 0,
      scale: 0.95,
      x: direction > 0 ? '100%' : '-100%',
    }),
    exit: (direction: number) => ({
      opacity: 0,
      scale: 0.95,
      x: direction < 0 ? '100%' : '-100%',
    }),
  };

  return (
    <div className="relative min-h-[250px] w-full overflow-hidden rounded-[10px] sm:min-h-[300px] md:min-h-[400px]">
      {/* Static content to maintain container height */}
      <div className="invisible">
        <SlideContent stepIndex={props.step} />
      </div>

      <AnimatePresence custom={props.direction}>
        <motion.div
          key={props.step}
          custom={props.direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            opacity: { duration: 0.2, ease: 'easeInOut' },
            scale: { duration: 0.2, ease: 'easeInOut' },
            x: { damping: 30, stiffness: 300, type: 'spring' },
          }}
          className="absolute inset-0 flex w-full items-center justify-center"
        >
          <SlideContent stepIndex={props.step} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function TestInstructionsClient({
  steps,
}: TestInstructionsClientProps): React.ReactElement {
  const [step, setStep] = React.useState<number>(0);
  const [direction, setDirection] = React.useState<number>(0);
  const isMobile = useIsMobile();
  const stepItems = React.useMemo(
    () => steps.map((_, i) => ({ index: i, label: `Step ${i + 1}` })),
    [steps]
  );

  const navButtons = (
    <div className="flex w-full max-w-[140px] items-center justify-between sm:max-w-[120px]">
      <button
        className="rounded-full p-2 transition-colors hover:bg-gray-100 disabled:opacity-50"
        disabled={step === 0}
        onClick={() => {
          if (step > 0) {
            setDirection(-1);
            setStep(step - 1);
          }
        }}
        aria-label="Previous step"
      >
        <Image
          height={40}
          width={40}
          alt="Previous step"
          src="/orange-gradient-left-icon.svg"
        />
      </button>
      <button
        disabled={step === steps.length - 1}
        className="rounded-full p-2 transition-colors hover:bg-gray-100 disabled:opacity-50"
        onClick={() => {
          if (step < steps.length - 1) {
            setDirection(1);
            setStep(step + 1);
          }
        }}
        aria-label="Next step"
      >
        <Image
          height={40}
          width={40}
          alt="Next step"
          src="/orange-gradient-right-icon.svg"
        />
      </button>
    </div>
  );

  return (
    <div className="content-wrapper mt-8 flex w-full flex-col items-center justify-center sm:mt-12">
      <div className="flex w-full flex-col rounded-[10px] pb-5">
        {!isMobile && <SliderContent step={step} direction={direction} />}
        <div className="mt-6 flex w-full max-w-[1100px] flex-col items-center gap-8 md:flex-row md:gap-12 md:self-center">
          <StepSelector
            items={stepItems}
            selectedItem={stepItems[step]}
            onItemSelect={item => {
              setDirection(item.index > step ? 1 : -1);
              setStep(item.index);
            }}
            getItemLabel={item => item.label}
            theme="benefits"
            className=""
          />

          <div className="hidden md:block">{navButtons}</div>
        </div>

        <div className="mt-6 mb-4 flex w-full flex-col sm:mt-12 md:items-center">
          <div className="mb-6">
            <h3 className="mb-3 font-['Work_Sans'] text-lg font-semibold text-gray-900 sm:text-xl">
              {steps[step].label}
            </h3>
            <p className="font-['Work_Sans'] text-base leading-6 font-normal tracking-[0em] text-gray-700 sm:text-lg sm:leading-7 md:text-[16px] md:leading-[20px]">
              {steps[step].description}
            </p>
          </div>
        </div>
        {isMobile && <SliderContent step={step} direction={direction} />}
        <div className="mt-6 flex w-full justify-center md:hidden">
          {navButtons}
        </div>
        <AnimatedGradientButton
          flatHover={true}
          text="Interpret My Results"
          href="/interpreting-results"
          as="anchor"
          theme="orange"
          className="mt-4 w-full max-w-[280px] self-center sm:max-w-[240px] sm:min-w-[180px]"
        />
      </div>
    </div>
  );
}
